/**
 * PrivacyPolicyPage - Politique de confidentialité (RGPD)
 * Conforme au Règlement Général sur la Protection des Données (UE) 2016/679
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  // Déclarer Twilio et un historique d'appels alors qu'aucun appel ni SMS ne
  // part serait inexact — la transparence RGPD porte sur les traitements réels.
  const voiceAssistEnabled = tenant?.config?.ecommerce?.voiceAssistEnabled ?? false;
  // Contrôle visuel des fruits et légumes à la préparation (12/10/2026)
  const controleVisuel = (tenant?.config?.ecommerce as any)?.preparation?.controleVisuel ?? {};
  const controleActif = controleVisuel.actif !== false;
  const conservationPhotos = Number(controleVisuel.conservationJours) || 15;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/shop')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Politique de confidentialité</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-sm text-gray-600 mb-8">Dernière mise à jour : 12 octobre 2026</p>

        {/* Introduction */}
        <section className="mb-10">
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              La présente politique de confidentialité décrit comment Les Folies Maraîchères SRL
              (ci-après « nous ») collecte, utilise et protège vos données personnelles lorsque vous
              utilisez notre site <strong>foliesboutik.be</strong> et nos services associés.
            </p>
            <p>
              Nous nous engageons à respecter votre vie privée conformément au Règlement Général sur la
              Protection des Données (RGPD - Règlement UE 2016/679) et à la législation belge applicable.
            </p>
          </div>
        </section>

        {/* Article 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Responsable du traitement</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p><strong>Les Folies Maraîchères SRL</strong></p>
            <p>Rue du Commerce 147<br />7370 Dour, Belgique</p>
            <p>Tél. : 0478/51.68.22<br />
            Email : contact@foliesboutik.be<br />
            T.V.A. 0715954426</p>
            <p>Responsable : Philippe Carré</p>
          </div>
        </section>

        {/* Article 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Données collectées</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>Nous collectons les catégories de données personnelles suivantes :</p>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Catégorie</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Données</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Finalité</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Base légale</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Identification</td>
                  <td className="border border-gray-300 px-4 py-3">Nom, prénom, email</td>
                  <td className="border border-gray-300 px-4 py-3">Création et gestion du compte client</td>
                  <td className="border border-gray-300 px-4 py-3">Exécution du contrat</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Contact</td>
                  <td className="border border-gray-300 px-4 py-3">Téléphone</td>
                  <td className="border border-gray-300 px-4 py-3">
                    {voiceAssistEnabled
                      ? 'Appel personnalisé pendant la préparation, notifications SMS'
                      : 'Vous contacter au sujet de votre commande'}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">Consentement</td>
                </tr>
                {controleActif && (
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Photos de contrôle de préparation</td>
                  <td className="border border-gray-300 px-4 py-3">Photo des fruits et légumes pesés pour votre commande, cadrée sur le plateau de la balance, rattachée à la ligne de commande</td>
                  <td className="border border-gray-300 px-4 py-3">Vérifier que l'article préparé correspond à l'article commandé, et traiter une éventuelle réclamation</td>
                  <td className="border border-gray-300 px-4 py-3">Intérêt légitime (qualité des préparations)</td>
                </tr>
                )}
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Adresse</td>
                  <td className="border border-gray-300 px-4 py-3">Rue, ville, code postal, coordonnées GPS</td>
                  <td className="border border-gray-300 px-4 py-3">Livraison des commandes</td>
                  <td className="border border-gray-300 px-4 py-3">Exécution du contrat</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Commandes</td>
                  <td className="border border-gray-300 px-4 py-3">Historique, articles, montants</td>
                  <td className="border border-gray-300 px-4 py-3">Traitement et suivi des commandes</td>
                  <td className="border border-gray-300 px-4 py-3">Exécution du contrat</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Paiement</td>
                  <td className="border border-gray-300 px-4 py-3">Montants, statut de paiement, solde du compte FOLIES</td>
                  <td className="border border-gray-300 px-4 py-3">Traitement des paiements</td>
                  <td className="border border-gray-300 px-4 py-3">Exécution du contrat</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Préférences</td>
                  <td className="border border-gray-300 px-4 py-3">Préférences alimentaires, produits favoris</td>
                  <td className="border border-gray-300 px-4 py-3">Personnalisation de l'expérience</td>
                  <td className="border border-gray-300 px-4 py-3">Consentement</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Communication</td>
                  <td className="border border-gray-300 px-4 py-3">
                    {voiceAssistEnabled ? 'Historique appels, SMS, emails' : 'Historique des emails'}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">Service client, suivi de commande</td>
                  <td className="border border-gray-300 px-4 py-3">Exécution du contrat / Intérêt légitime</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Technique</td>
                  <td className="border border-gray-300 px-4 py-3">Tokens push, type d'appareil</td>
                  <td className="border border-gray-300 px-4 py-3">Envoi de notifications push</td>
                  <td className="border border-gray-300 px-4 py-3">Consentement</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Lettre d'actualités</td>
                  <td className="border border-gray-300 px-4 py-3">Adresse email, date, adresse IP et navigateur au moment de l'inscription (preuve du consentement)</td>
                  <td className="border border-gray-300 px-4 py-3">Envoi de nos actualités par email, après confirmation par clic</td>
                  <td className="border border-gray-300 px-4 py-3">Consentement — désinscription en un clic dans chaque email</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 prose prose-lg text-gray-700 leading-relaxed">
            <p>
              <strong>Note importante :</strong> Nous ne collectons jamais vos données bancaires.
              Les paiements par carte sont traités directement par Stripe, certifié PCI-DSS.
            </p>
          </div>
        </section>

        {/* Article 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Sous-traitants et transferts de données</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Nous faisons appel aux sous-traitants suivants pour le fonctionnement de nos services :
            </p>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Sous-traitant</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Finalité</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Données transmises</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Localisation</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Stripe</td>
                  <td className="border border-gray-300 px-4 py-3">Paiement en ligne</td>
                  <td className="border border-gray-300 px-4 py-3">Email, nom, montant</td>
                  <td className="border border-gray-300 px-4 py-3">USA (clauses contractuelles types)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Brevo (Sendinblue)</td>
                  <td className="border border-gray-300 px-4 py-3">Emails transactionnels et lettre d'actualités (sur abonnement)</td>
                  <td className="border border-gray-300 px-4 py-3">Email, nom, détails commande</td>
                  <td className="border border-gray-300 px-4 py-3">France / UE</td>
                </tr>
                {voiceAssistEnabled && (
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">Twilio</td>
                  <td className="border border-gray-300 px-4 py-3">SMS et appels téléphoniques</td>
                  <td className="border border-gray-300 px-4 py-3">Téléphone, nom</td>
                  <td className="border border-gray-300 px-4 py-3">USA (clauses contractuelles types)</td>
                </tr>
                )}
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 font-medium">Firebase (Google)</td>
                  <td className="border border-gray-300 px-4 py-3">Notifications push</td>
                  <td className="border border-gray-300 px-4 py-3">Token appareil, type d'appareil</td>
                  <td className="border border-gray-300 px-4 py-3">USA (clauses contractuelles types)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 font-medium">OVH</td>
                  <td className="border border-gray-300 px-4 py-3">Hébergement et stockage</td>
                  <td className="border border-gray-300 px-4 py-3">Toutes les données</td>
                  <td className="border border-gray-300 px-4 py-3">France / UE</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Les transferts de données vers les États-Unis sont encadrés par des clauses contractuelles types
              (CCT) approuvées par la Commission européenne, conformément à l'article 46 du RGPD.
            </p>
            <p>
              <strong>Mesure d'audience :</strong> nous utilisons Plausible Analytics, un outil de statistiques
              respectueux de la vie privée, auto-hébergé sur nos propres serveurs dans l'Union européenne.
              Il ne dépose aucun cookie, ne collecte aucune donnée personnelle identifiable et ne suit pas
              les visiteurs d'un site à l'autre. Aucune donnée n'est transmise à un tiers.
            </p>
          </div>
        </section>

        {/* Article 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Durée de conservation</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Catégorie de données</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Durée de conservation</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Justification</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr>
                  <td className="border border-gray-300 px-4 py-3">Compte client</td>
                  <td className="border border-gray-300 px-4 py-3">Durée du compte + 3 ans après suppression</td>
                  <td className="border border-gray-300 px-4 py-3">Prescription légale</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3">Données de commande et facturation</td>
                  <td className="border border-gray-300 px-4 py-3">7 ans</td>
                  <td className="border border-gray-300 px-4 py-3">Obligation légale comptable et fiscale (Belgique)</td>
                </tr>
                {voiceAssistEnabled && (
                <tr>
                  <td className="border border-gray-300 px-4 py-3">Historique appels et SMS</td>
                  <td className="border border-gray-300 px-4 py-3">1 an</td>
                  <td className="border border-gray-300 px-4 py-3">Suivi qualité du service</td>
                </tr>
                )}
                {controleActif && (
                <tr>
                  <td className="border border-gray-300 px-4 py-3">Photos de contrôle de préparation</td>
                  <td className="border border-gray-300 px-4 py-3">{conservationPhotos} jours, puis effacement automatique</td>
                  <td className="border border-gray-300 px-4 py-3">Délai de réclamation sur une commande</td>
                </tr>
                )}
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3">Tokens notifications push</td>
                  <td className="border border-gray-300 px-4 py-3">Jusqu'à désinscription ou suppression du compte</td>
                  <td className="border border-gray-300 px-4 py-3">Consentement</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3">Cookies de session</td>
                  <td className="border border-gray-300 px-4 py-3">Durée de la session</td>
                  <td className="border border-gray-300 px-4 py-3">Nécessaire au fonctionnement</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Article 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Vos droits</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul>
              <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification</strong> : corriger des données inexactes ou incomplètes</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données (sous réserve des obligations légales de conservation)</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré et lisible par machine (JSON)</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données à des fins de marketing</li>
              <li><strong>Droit à la limitation</strong> : demander la restriction du traitement dans certains cas</li>
              <li><strong>Droit de retrait du consentement</strong> : retirer votre consentement à tout moment pour les traitements basés sur le consentement</li>
            </ul>
            <p>
              Vous pouvez exercer ces droits directement depuis votre espace client (profil, préférences)
              ou en nous contactant à l'adresse <strong>contact@foliesboutik.be</strong>.
            </p>
            <p>
              Nous nous engageons à répondre à votre demande dans un délai d'un mois. En cas de demande
              complexe, ce délai peut être prolongé de deux mois supplémentaires, après notification.
            </p>
          </div>
        </section>

        {/* Article 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Sécurité des données</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos
              données contre la perte, l'accès non autorisé et la divulgation : communications chiffrées,
              protection des mots de passe et hébergement sécurisé en Europe.
            </p>
            <p>
              Les paiements par carte bancaire sont traités par notre prestataire <strong>Stripe</strong>,
              certifié <strong>PCI-DSS</strong> : aucune donnée bancaire n'est stockée sur nos serveurs.
            </p>
          </div>
        </section>

        {/* Article 7 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Notre site utilise uniquement des cookies strictement nécessaires au fonctionnement du service.
              Notre outil de mesure d'audience (Plausible Analytics, auto-hébergé) fonctionne sans cookies.
              Pour plus d'informations, consultez notre{' '}
              <button
                onClick={() => navigate('/shop/cookies')}
                className="text-green-700 hover:text-green-700 underline font-medium"
              >
                Politique de cookies
              </button>.
            </p>
          </div>
        </section>

        {/* Article 8 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Réclamation</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD,
              vous avez le droit d'introduire une réclamation auprès de l'autorité de contrôle compétente :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="font-bold">Autorité de protection des données (APD)</p>
              <p>Rue de la Presse 35<br />1000 Bruxelles<br />Belgique</p>
              <p>Tél. : +32 (0)2 274 48 00<br />
              Email : contact@apd-gba.be<br />
              Site : www.autoriteprotectiondonnees.be</p>
            </div>
          </div>
        </section>

        {/* Article 9 */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Modification de cette politique</h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed">
            <p>
              Nous nous réservons le droit de modifier la présente politique de confidentialité à tout moment.
              En cas de modification substantielle, nous en informerons les utilisateurs par email ou par
              notification sur le site. La version en vigueur est toujours accessible sur cette page.
            </p>
          </div>
        </section>

        {/* Bouton retour */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/shop')}
            className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg transition-opacity hover:opacity-90"
          >
            Retour à la boutique
          </button>
        </div>
      </main>

    </div>
  );
}
